import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbp } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { ordinal } from "@/components/prohub/shared";

export default function BillImportDialog({ open, onOpenChange, onImported }) {
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    api.get("/pro-hub/bills/suggestions").then(({ data }) => {
      setItems(data.suggestions);
      setPicked(data.suggestions.filter((s) => s.category !== "Subscriptions").map((s) => s.merchant));
    }).catch(() => setItems([]));
  }, [open]);

  const toggle = (m) => setPicked((p) => (p.includes(m) ? p.filter((x) => x !== m) : [...p, m]));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/pro-hub/bills/import", { merchants: picked });
      toast.success(`Imported ${data.imported} ${data.imported === 1 ? "bill" : "bills"}`);
      onOpenChange(false);
      onImported();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pro-import-modal" className="bg-[#14181D] border-white/10 text-white w-[calc(100%-2rem)] max-w-md rounded-2xl sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Import from your bank</DialogTitle>
          <DialogDescription className="text-slate-400">We found these monthly payments in your connected accounts. Pick the ones to track as bills.</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
          {items === null && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#00C896]" /></div>}
          {items?.length === 0 && <p data-testid="pro-import-empty" className="py-6 text-center text-sm text-slate-500">No new recurring payments found. Connect a bank or add bills manually.</p>}
          {items?.map((s) => (
            <label key={s.merchant} data-testid={`pro-import-row-${s.merchant.replace(/\W+/g, "-").toLowerCase()}`} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#0D0D0D] px-3 py-2.5 cursor-pointer hover:border-white/20 transition-colors">
              <Checkbox checked={picked.includes(s.merchant)} onCheckedChange={() => toggle(s.merchant)} className="border-white/30 data-[state=checked]:bg-[#00C896] data-[state=checked]:border-[#00C896]" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm truncate">{s.merchant}</span>
                <span className="block text-[11px] text-slate-500">{s.category} · around the {ordinal(s.due_day)}</span>
              </span>
              <span className="font-num text-sm font-semibold">{gbp(s.amount)}</span>
            </label>
          ))}
        </div>
        <Button data-testid="pro-import-save" onClick={save} disabled={saving || picked.length === 0} className="w-full rounded-xl bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Import ${picked.length} selected`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
